import type { CardLayout, OcclusionStudyMode, ReviewCard, ReviewMask } from '../../../sdk/@types/Revisao';
import type { FormaAutoria } from './formaAutoria';

/**
 * Baralho e tags do compositor. A API só grava `tags`. O baralho escolhido
 * viaja na primeira posição — é a ficha que `ReviewCard` já lê de `tags[0]`.
 * As demais posições são as tags soltas. Um cartão antigo, sem esta barra,
 * reabre com a primeira tag no baralho e as outras nas tags; gravar de novo
 * devolve a mesma ordem.
 */
export interface MetadadosDoCartao {
  baralho: string;
  tags: string[];
}

/** Uma linha de "Criados agora". `card` reabre `/admin/cartoes/:id/editar`. */
export interface CartaoCriadoAgora {
  id: string;
  /** Frente já gravada (no reverso, a frente original trocada). */
  rotulo: string;
  reverso: boolean;
  card: ReviewCard;
}

export function tagsParaGravar(baralho: string, tags: readonly string[]): string[] {
  const gravadas: string[] = [];
  const vistas = new Set<string>();
  const acrescentar = (valor: string) => {
    const limpa = valor.trim();
    if (!limpa || vistas.has(limpa)) return;
    vistas.add(limpa);
    gravadas.push(limpa);
  };
  acrescentar(baralho);
  tags.forEach(acrescentar);
  return gravadas;
}

export function metadadosDasTags(tags: readonly string[] | undefined): MetadadosDoCartao {
  const limpas = (tags ?? []).map((tag) => tag.trim()).filter(Boolean);
  const [baralho, ...resto] = limpas;
  return { baralho: baralho ?? '', tags: resto };
}

/** Oclusão não tem duas faces de texto para inverter. */
export function deveCriarReverso(forma: FormaAutoria, pedido: boolean): boolean {
  return pedido && forma !== 'oclusao';
}

export interface ParDeFaces {
  front?: string;
  back?: string;
}

/**
 * Um envio com reverso devolve o par escrito e, em seguida, o par trocado.
 * Oclusão não passa daqui: `deveCriarReverso` já recusou o pedido.
 */
export function facesDoEnvio(faces: ParDeFaces, criarReverso: boolean): ParDeFaces[] {
  if (!criarReverso) return [faces];
  return [faces, { front: faces.back, back: faces.front }];
}

export function rotuloDoCartao(layout: CardLayout, front: string | undefined): string {
  if (layout === 'occlusion') return 'Oclusão';
  const texto = (front ?? '').replace(/\s+/g, ' ').trim();
  return texto || 'Cartão sem texto';
}

export function montarCartaoCriado(args: {
  id: string;
  reverso: boolean;
  layout: CardLayout;
  subjectId: string;
  tags: string[];
  front?: string;
  back?: string;
  frontImageKey?: string;
  masks?: ReviewMask[];
  studyMode?: OcclusionStudyMode;
}): CartaoCriadoAgora {
  const oclusao = args.layout === 'occlusion';
  const card: ReviewCard = {
    id: args.id,
    source: 'flashcard',
    layout: args.layout,
    subjectId: args.subjectId,
    tags: [...args.tags],
    status: 'new',
    dueDate: '',
    intervalDays: 0,
    front: oclusao ? undefined : args.front,
    back: args.back,
    frontImageKey: oclusao ? args.frontImageKey : undefined,
    masks: oclusao ? (args.masks ? [...args.masks] : undefined) : undefined,
    studyMode: oclusao ? args.studyMode : undefined,
  };
  return {
    id: args.id,
    rotulo: rotuloDoCartao(args.layout, args.front),
    reverso: args.reverso,
    card,
  };
}

/**
 * Cartões criados nesta carga da página. Memória de módulo de propósito:
 * recarregar zera a lista; `localStorage` mostraria os de ontem;
 * `sessionStorage` sobreviveria ao reload. Navegar para editar e voltar
 * a `/admin/cartoes/novo` ainda encontra a mesma lista.
 */
const sessao: CartaoCriadoAgora[] = [];

export function listarCriadosAgora(): CartaoCriadoAgora[] {
  return sessao.map((item) => item);
}

export function registrarCriadosAgora(itens: readonly CartaoCriadoAgora[]): CartaoCriadoAgora[] {
  for (const item of itens) {
    if (item.id) sessao.push(item);
  }
  return listarCriadosAgora();
}
